/**
 * D454: the two small things the producer list remembers on this device. The Producers
 * header's switch (x1, x10, Next, Max; Next by default) and the first-time tip over the list,
 * which goes once the player has tapped a circle three times or closed it. Both are kept in
 * localStorage, read once; storage that throws keeps them for this session only.
 */
import { useEffect, useState } from 'preact/hooks';
import type { BuyMode } from './view.ts';

export const BUY_MODE_KEY = 'hedgerow-buy-mode';
export const CIRCLE_TIP_KEY = 'hedgerow-circle-tip';
/** The tip goes after this many taps on a circle. */
export const TIP_TAPS = 3;

export const BUY_MODES: readonly BuyMode[] = ['one', 'ten', 'next', 'max'];
/** The words on the switch's segments. */
export const BUY_MODE_LABEL: Record<BuyMode, string> = {
  one: '×1',
  ten: '×10',
  next: 'Next',
  max: 'Max',
};

function get(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function put(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // Kept for this session only.
  }
}

function readMode(): BuyMode {
  const v = get(BUY_MODE_KEY);
  return BUY_MODES.find((m) => m === v) ?? 'next';
}

let mode: BuyMode = readMode();
const modeListeners = new Set<(m: BuyMode) => void>();

export function getBuyMode(): BuyMode {
  return mode;
}

export function setBuyMode(value: BuyMode): void {
  mode = value;
  put(BUY_MODE_KEY, value);
  for (const fn of modeListeners) fn(value);
}

/** The list's switch: `[mode, set]`. */
export function useBuyMode(): [BuyMode, (m: BuyMode) => void] {
  const [value, setValue] = useState(mode);
  useEffect(() => {
    modeListeners.add(setValue);
    setValue(mode);
    return () => void modeListeners.delete(setValue);
  }, []);
  return [value, setBuyMode];
}

/** Taps on a circle so far, or `TIP_TAPS` once the tip was closed. */
function readTaps(): number {
  const v = get(CIRCLE_TIP_KEY);
  const n = v === null ? 0 : Number(v);
  return Number.isFinite(n) && n > 0 ? Math.min(TIP_TAPS, Math.floor(n)) : 0;
}

let taps = readTaps();
const tipListeners = new Set<(n: number) => void>();

function setTaps(n: number): void {
  taps = Math.min(TIP_TAPS, n);
  put(CIRCLE_TIP_KEY, String(taps));
  for (const fn of tipListeners) fn(taps);
}

/** A tap on a circle counts toward the tip going. */
export function countCircleTap(): void {
  if (taps < TIP_TAPS) setTaps(taps + 1);
}

export function closeCircleTip(): void {
  setTaps(TIP_TAPS);
}

export function circleTipShown(): boolean {
  return taps < TIP_TAPS;
}

/** The tip over the list: whether it shows. */
export function useCircleTip(): boolean {
  const [n, setN] = useState(taps);
  useEffect(() => {
    tipListeners.add(setN);
    setN(taps);
    return () => void tipListeners.delete(setN);
  }, []);
  return n < TIP_TAPS;
}
